import moment, { months } from 'moment'
import { Fragment, useContext, useEffect } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import { classes, style } from 'typestyle'

/* Context ====================================================================================== */
import { NavigationContext } from '../../__aeki__/contexts/navigation'

/* Core ========================================================================================= */
import { addSnippet } from '../../__aeki__/core/snippet-manager'

/* Hooks ======================================================================================== */
import useFetch from '../../__aeki__/hooks/use-fetch'
import useValue from '../../__aeki__/hooks/use-value'

/* Components =================================================================================== */
import { Box } from '../../__aeki__/components/box/box.component'
import { Button } from '../../__aeki__/components/button'
import { Circle } from '../../__aeki__/components/circle'
import { Line } from '../../__aeki__/components/line'
import { Snippet, SnippetHead } from '../../__aeki__/modules/layouts/snippet'
import { Text } from '../../__aeki__/components/text/text.component'

/* Styles ======================================================================================= */
import { fitWidth } from '../../__aeki__/styles/styleset/fit-width/fitWidth'
import { f18i } from '../../__aeki__/styles/styleset/font-size/f18i'
import { ml16 } from '../../__aeki__/styles/styleset/margin/ml16'
import { ml8 } from '../../__aeki__/styles/styleset/margin/ml8'
import { mr4 } from '../../__aeki__/styles/styleset/margin/mr4'
import { p0 } from '../../__aeki__/styles/styleset/padding/p0'
import { pl8 } from '../../__aeki__/styles/styleset/padding/pl8'
import { pr8 } from '../../__aeki__/styles/styleset/padding/pr8'
import { textCenter } from '../../__aeki__/styles/styleset/text-center/textCenter'
import { pb12 } from '../../__aeki__/styles/styleset/padding/pb12'
import { pt12 } from '../../__aeki__/styles/styleset/padding/pt12'
import { paddingLeftInner } from '../../__aeki__/styles/default/padding'
import { pl16 } from '../../__aeki__/styles/styleset/padding/pl16'
import { pr16 } from '../../__aeki__/styles/styleset/padding/pr16'

const DAYS_IN_WEEK = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun']
const DAYS = ['sun', 'mon', 'tue', 'wed', 'thu', 'fri', 'sat']

const today = new Date()

const getDaysOfWeek = (ref: any) => {
  const today = new Date(ref)
  const dates: any = []

  if (today.getDay() === 0) {
    for (let i = 0; i < 7; i++) {
      const refDate = new Date(today)
      refDate.setDate(today.getDate() - (today.getDay() - 1 - i))
      dates.push(refDate)
    }
  } else {
    for (let i = 0; i < today.getDay() - 1; i++) {
      const refDate = new Date(today)
      refDate.setDate(today.getDate() - (today.getDay() - 1 - i))
      dates.push(refDate)
    }

    dates.push(today)

    for (let i = 1; i <= 7 - today.getDay(); i++) {
      const refDate = new Date(today)
      refDate.setDate(today.getDate() + i)
      dates.push(refDate)
    }
  }

  const nextStartDate = new Date(dates[0])
  const nextEndDate = new Date(dates[6])

  nextEndDate.setDate(today.getDate() - 1)
  nextStartDate.setDate(nextEndDate.getDate() - 7)

  const prevStartDate = new Date(dates[0])
  const prevEndDate = new Date(dates[6])

  prevStartDate.setDate(prevStartDate.getDate() + 7)
  prevEndDate.setDate(prevStartDate.getDate() + 7)

  return {
    dates,
    next: {
      start: nextStartDate,
      end: nextEndDate,
    },
    prev: {
      start: prevStartDate,
      end: prevEndDate,
    },
  }
}

export const DailyInfoSpit = ({ children, groupId, rowId, refDate, update }: any) => {
  const apiUrl = `${process.env.REACT_APP_API_URL}/a/snippets/calendar/${groupId}/${rowId}/${refDate}`
  const [data, $data] = useFetch({}, { url: apiUrl })

  useEffect(() => {
    if (data.mounted) {
      $data.get({
        url: `${apiUrl}`,
      })
    }
  }, [data.mounted, refDate, update])

  return children(data)
}

export default addSnippet('Calendar', (props: any) => {
  const { title, row } = props
  const { navigation, layout, schema } = useContext(NavigationContext)
  const { groupId, rowId } = useParams()

  const apiUrl = `${process.env.REACT_APP_API_URL}/a/snippets/calendar/${rowId}`
  const postUrl = `${process.env.REACT_APP_API_URL}/a/snippets/calendar/${groupId}/${rowId}/add`
  const [data, $data] = useFetch({ parsed: {} }, { url: apiUrl })
  const [post, $post] = useFetch({ parsed: {} }, { url: postUrl })
  const [index, $index] = useValue(0)
  const [date, $date] = useValue(new Date())
  const [days, $days] = useValue(getDaysOfWeek(date))
  const history = useNavigate()

  useEffect(() => {
    const refDate = new Date()
    refDate.setDate(refDate.getDate() + index * 7)
    $days.set(getDaysOfWeek(refDate))

    if (data.mounted) {
      $data.get({ url: `${process.env.REACT_APP_API_URL}/a/snippets/calendar/${rowId}/${index}` })
    }
  }, [index, data.mounted, props.trigger])

  const available: any = {}

  Object.keys(
    row?.body?.hours?.customHours ? row?.body?.hours || {} : row?.body?.program?.body?.hours || {},
  ).map((key: any) => {
    if (row?.body?.hours?.customHours) {
      if (
        !row?.body?.hours[key].closed &&
        row?.body?.hours[key].open &&
        row?.body?.hours[key].close
      ) {
        available[key] = true
      }
    } else {
      if (
        !row?.body?.program?.body?.hours[key].closed &&
        row?.body?.program?.body?.hours[key].open &&
        row?.body?.program?.body?.hours[key].close
      ) {
        available[key] = true
      }
    }
  })

  return (
    <Snippet>
      <SnippetHead
        title={`Weekly Calendar | ${
          months()[days.dates[0].getMonth()]
        } ${days.dates[0].getFullYear()}`}
      >
        <Box className={fitWidth} flex baseline="none">
          <Button
            className={mr4}
            icon={<span className={classes('material-icons', f18i)}>keyboard_arrow_left</span>}
            baseline={'surface'}
            size="sm"
            onClick={() => $index.set((prev: number) => prev - 1)}
          />

          <Button
            icon={<span className={classes('material-icons', f18i)}>keyboard_arrow_right</span>}
            baseline={'surface'}
            iconPosition={'right'}
            size="sm"
            onClick={() => $index.set((prev: number) => prev + 1)}
          />
        </Box>
      </SnippetHead>
      <Line />
      <Box baseline="surface" flex spaceBetween rounded>
        <Box baseline="surface" row spaceBetween rounded fullWidth>
          {days.dates.map((item: any, key: any) => {
            const refDate = new Date(item)

            const { incident, incidentDetails, request, requestDetails } =
              data.payload.parsed[moment(item).format('MMM, DD, YYYY')]?.body || {}

            return (
              <Fragment key={key}>
                <Box
                  baseline="none"
                  className={classes(
                    // p16,
                    style({
                      background: !available[DAYS[refDate.getDay()]] ? 'rgb(250,250,250)' : '',
                    }),
                  )}
                  flex
                  spaceBetween
                  fullWidth
                  alignCenterY
                >
                  <Box flex baseline="none">
                    <Box flex alignCenterY baseline="none" className={classes(pl16, pr16)}>
                      <Circle
                        size="md"
                        style={{ width: 32, height: 32 }}
                        baseline={
                          moment(item).format('MMM, DD, YYYY') ==
                          moment(today).format('MMM, DD, YYYY')
                            ? 'primary'
                            : 'background'
                        }
                      >
                        <Text
                          baseline={
                            moment(item).format('MMM, DD, YYYY') ==
                            moment(today).format('MMM, DD, YYYY')
                              ? 'primary'
                              : 'background'
                          }
                          lighter={
                            moment(item).format('MMM, DD, YYYY') !==
                            moment(today).format('MMM, DD, YYYY')
                          }
                          size="xs"
                          shrink
                          // semiBold
                          uppercase
                          style={{ fontSize: 10 }}
                        >
                          {DAYS_IN_WEEK[key]}
                        </Text>
                      </Circle>
                      <Text size="sm" className={classes(textCenter, ml8)} shrink>
                        {moment(item).format('MMM DD')}
                      </Text>
                    </Box>
                    <Line className={ml16} horiz />
                    <Box row className={classes(ml16, pt12, pb12)} alignCenterY baseline="none">
                      {available[DAYS[refDate.getDay()]] ? (
                        <table className={p0}>
                          <tr className={p0}>
                            <td className={classes(pl8, pr8)}>
                              <Text size="sm" shrink>
                                Attendance:
                              </Text>
                            </td>
                            <td className={classes(pr8)}>
                              <Text size="sm" shrink>
                                <DailyInfoSpit
                                  groupId={groupId}
                                  rowId={rowId}
                                  refDate={moment(item).toISOString()}
                                  update={props.trigger}
                                >
                                  {temp => {
                                    if (Object.keys(temp.payload).length > 0) {
                                      return `${temp?.payload?.body?.attendance?.attendance.filter(
                                        a => a.in && !a.absent,
                                      ).length || 0} / ${temp.payload?.body?.attendance?.attendance
                                        ?.length ||
                                        temp.payload?.body?.students?.length ||
                                        0}`
                                    } else {
                                      return `0 / ${row.body.students?.length || 0}`
                                    }
                                  }}
                                </DailyInfoSpit>
                              </Text>
                            </td>
                          </tr>
                        </table>
                      ) : (
                        <table className={p0}>
                          <tr className={p0}>
                            <td className={classes(pl8, pr8)}>
                              <Text size="sm" shrink italic baseline="surface" lighter>
                                No class
                              </Text>
                            </td>
                          </tr>
                        </table>
                      )}
                    </Box>
                  </Box>
                  {/* {key + 1} */}
                  <Box baseline="none" className={paddingLeftInner}>
                    {available[DAYS[refDate.getDay()]] ? (
                      <Button
                        onClick={() => {
                          if (!post.meta.isLoading) {
                            if (data.payload.parsed[moment(item).format('MMM, DD, YYYY')]) {
                              history(
                                `/a/groups/${groupId}/base/childcare-classes-dailies/table/${
                                  data.payload.parsed[moment(item).format('MMM, DD, YYYY')].id
                                }`,
                              )
                            } else {
                              $post
                                .post({
                                  body: {
                                    body: {
                                      date: item.toISOString(),
                                    },
                                  },
                                })
                                .then((payload: any) => {
                                  history(
                                    `/a/groups/${groupId}/base/childcare-classes-dailies/table/${payload.id}`,
                                  )
                                })
                            }
                          }
                        }}
                        size="sm"
                        label={
                          data.payload.parsed[moment(item).format('MMM, DD, YYYY')]
                            ? 'Go to Attendance'
                            : 'Go to Attendance'
                        }
                        baseline={
                          data.payload.parsed[moment(item).format('MMM, DD, YYYY')]
                            ? 'primary'
                            : 'primary'
                        }
                        square
                      />
                    ) : (
                      // <Button size="sm" label={'No Class'} square baseline={'disabled'} />
                      ''
                    )}
                  </Box>
                </Box>
                {key < days.dates.length - 1 && <Line />}
              </Fragment>
            )
          })}
        </Box>
      </Box>
    </Snippet>
  )
})
